import {
  CheckCircle,
  CloudDownload,
  ContentCopy,
  Download,
  Image as ImageIcon,
  Mail,
  PhoneIphone,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  usePhotoDownload,
  type DownloadProgress,
} from "../hooks/usePhotoDownload";
import type { Order, Photo } from "../types/api";

interface PhotoWithUrl extends Photo {
  downloading?: boolean;
  downloadUrl?: string;
}

const PaymentSuccess: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { downloadSingle, downloadZip } = usePhotoDownload();

  // State
  const [order, setOrder] = useState<Order | null>(null);
  const [photos, setPhotos] = useState<PhotoWithUrl[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] =
    useState<DownloadProgress | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Fetch order and photos
  useEffect(() => {
    const fetchPaymentData = async () => {
      if (!token) {
        setError("Invalid payment link - missing token");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch order
        const orderResponse = await fetch(`/api/orders/public/${token}`);

        if (!orderResponse.ok) {
          throw new Error("Failed to load order information");
        }

        const orderData = await orderResponse.json();

        // Verify order is actually paid
        if (orderData.order.status === "pending_payment") {
          setError(
            "Updating payment status for the order. Please come back after a few minutes."
          );
          setTimeout(() => {
            window.location.reload();
          }, 3000);
          return;
        }

        setOrder(orderData.order);

        // Fetch photos
        const photosResponse = await fetch(
          `/api/orders/public/${token}/photos`
        );

        if (photosResponse.ok) {
          const photosData = await photosResponse.json();
          setPhotos(photosData);
        }
      } catch (err) {
        console.error("Error fetching payment data:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load payment confirmation"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, [token]);

  // Handle individual photo download
  const handleDownloadPhoto = async (_photoId: string, index: number) => {
    try {
      setPhotos((photos) => {
        const newPhotos = [...photos];
        newPhotos[index].downloading = true;
        return newPhotos;
      });
      setDownloadError(null);

      const photo = photos[index];
      const filename =
        photo.metadata?.originalFileName ||
        `${order?.id}_photo_${String(index + 1).padStart(3, "0")}.jpg`;

      await downloadSingle(photo, filename);

      setPhotos((photos) => {
        const newPhotos = [...photos];
        newPhotos[index].downloading = false;
        return newPhotos;
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to download photo";
      console.error("Error downloading photo:", errorMessage);
      setDownloadError(errorMessage);

      setPhotos((photos) => {
        const newPhotos = [...photos];
        newPhotos[index].downloading = false;
        return newPhotos;
      });
    }
  };

  // Handle download all photos as ZIP
  const handleDownloadAll = async () => {
    try {
      setDownloadingAll(true);
      setDownloadError(null);
      setDownloadProgress(null);

      const zipName = `order_${order?.id}_photos.zip`;

      const result = await downloadZip(photos, zipName, (progress) => {
        setDownloadProgress(progress);
      });

      setDownloadingAll(false);
      setDownloadProgress(null);

      // Show summary message
      if (result.failed > 0) {
        setDownloadError(
          `Downloaded ${result.successful} of ${photos.length} photos. ${result.failed} photo(s) failed to download.`
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to download photos";
      console.error("Error downloading all photos:", errorMessage);
      setDownloadError(errorMessage);
      setDownloadingAll(false);
      setDownloadProgress(null);
    }
  };

  // Handle copy deep link to clipboard
  const handleCopyDeepLink = () => {
    const deepLink = `icare-lullaboo://home/photo/request-detail/${order?.id}`;
    navigator.clipboard.writeText(deepLink).then(() => {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#faf5ff",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading your confirmation...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Order information not found</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#faf5ff", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Success Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <CheckCircle
              sx={{
                fontSize: 80,
                color: "#10b981",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.7 },
                  "100%": { opacity: 1 },
                },
              }}
            />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
            Payment Successful!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {order?.digitalAvailable
              ? "Thank you for your purchase. Your photos are ready to download."
              : "Thank you for your purchase. Your order has been confirmed."}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Left Column: Order Confirmation */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Order Confirmation
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Order ID
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor: "#f1f5f9",
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      {order.id}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Amount Paid
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: "primary.main", fontWeight: 600 }}
                    >
                      ${order.transaction?.amount?.toFixed(2) || "0.00"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 18, color: "#10b981" }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Paid
                      </Typography>
                    </Box>
                  </Box>

                  {order.parentInfo && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Confirmation Sent To
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Mail sx={{ fontSize: 18 }} color="info" />
                        <Typography variant="body2">
                          {order.parentInfo.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Deep Link to iCare App */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <PhoneIphone sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Open in iCare App
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Access your order directly from the iCare Portraits mobile
                  app:
                </Typography>

                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={<PhoneIphone />}
                    href={`icare-lullaboo://home/photo/request-detail/${order.id}`}
                  >
                    Open in App
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<ContentCopy />}
                    onClick={handleCopyDeepLink}
                  >
                    {copyFeedback ? "Copied!" : "Copy Link"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Photo Downloads or Processing Message */}
          <Grid size={{ xs: 12, md: 7 }}>
            {order.digitalAvailable && order.status === "fulfilled" ? (
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <CloudDownload sx={{ color: "primary.main" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Download Your Photos
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  {downloadError && (
                    <Alert
                      severity={
                        downloadError.includes("Downloaded") ? "info" : "error"
                      }
                      onClose={() => setDownloadError(null)}
                      sx={{ mb: 3 }}
                    >
                      {downloadError}
                    </Alert>
                  )}

                  {photos.length > 0 ? (
                    <>
                      {/* Download All Button */}
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Download />}
                        fullWidth
                        onClick={handleDownloadAll}
                        disabled={downloadingAll}
                        sx={{ mb: 3, py: 1.25 }}
                      >
                        {downloadingAll
                          ? "Preparing ZIP..."
                          : "Download All Photos (ZIP)"}
                      </Button>

                      {downloadingAll && (
                        <Box sx={{ mb: 3 }}>
                          <LinearProgress
                            variant="determinate"
                            value={downloadProgress?.percentage || 0}
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {downloadProgress?.current || 0} of {photos.length}{" "}
                            photos
                            {downloadProgress?.currentFileName &&
                              ` • ${downloadProgress.currentFileName}`}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ mb: 3 }}>Or</Divider>

                      {/* Individual Photos */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Download individual photos:
                      </Typography>

                      <Grid container spacing={2}>
                        {photos.map((photo, index) => (
                          <Grid size={{ xs: 6, sm: 4, md: 3 }} key={photo.id}>
                            <Card
                              sx={{
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": {
                                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                },
                              }}
                            >
                              {/* Photo Thumbnail */}
                              <Box
                                sx={{
                                  position: "relative",
                                  overflow: "hidden",
                                  backgroundColor: "#f1f5f9",
                                  paddingBottom: "100%",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={photo.finalUrl}
                                  alt={`Photo ${index + 1}`}
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />

                                {/* Download Overlay */}
                                {photo.downloading && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CircularProgress size={30} />
                                  </Box>
                                )}
                              </Box>

                              {/* Download Button */}
                              <Box sx={{ p: 1 }}>
                                <Button
                                  size="small"
                                  fullWidth
                                  variant="outlined"
                                  startIcon={<Download />}
                                  onClick={() =>
                                    handleDownloadPhoto(photo.id, index)
                                  }
                                  disabled={!!photo.downloading}
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {photo.downloading ? "..." : "Download"}
                                </Button>
                              </Box>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <ImageIcon
                        sx={{
                          fontSize: 60,
                          color: "text.disabled",
                          mb: 2,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No photos found in this order
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <CheckCircle
                      sx={{
                        fontSize: 60,
                        color: "#fbbf24",
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Order Being Processed
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your order includes physical photos that will be delivered
                      to your campus. Digital photos will be available for
                      download once production is complete.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* Footer CTA */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            variant="text"
            onClick={() => navigate("/")}
            sx={{ color: "primary.main" }}
          >
            Return to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PaymentSuccess;
